import {
  DEFAULT_LOCALE,
  LOCALE_CODES,
  type Locale,
  formatDate as fmtDate,
  formatDateTime as fmtDateTime,
  formatMoney as fmtMoney,
  formatNumber as fmtNumber,
  getTranslator,
  isLocale,
  isRtl,
} from '@upweo/i18n';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { I18nContext } from './i18n-context.js';
import type { I18nValue } from './i18n-types.js';

/**
 * React bindings for @upweo/i18n, shared by all three apps.
 *
 * Lives here rather than in each app so there is exactly one implementation of
 * "which language am I in" — three copies would drift, and the storefront's
 * copy is the one a customer sees.
 *
 * Deliberately NOT a translation library. The catalogue is a plain object that
 * ships in the bundle: six languages of UI strings is a few kilobytes, and
 * lazy-loading them would trade that for a flash of untranslated text on every
 * first paint.
 */

/**
 * What language to start in, in order of how strongly it signals intent:
 * an explicit past choice, then a value the server decided (the storefront
 * negotiates per store), then the browser, then English.
 */
const initialLocale = (storageKey: string, preferred?: Locale): Locale => {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(storageKey);
    if (stored !== null && isLocale(stored)) {
      return stored;
    }
  }
  if (preferred && isLocale(preferred)) {
    return preferred;
  }
  if (typeof navigator !== 'undefined') {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const primary = tag.split('-')[0] ?? '';
      if (isLocale(primary)) {
        return primary;
      }
    }
  }
  return DEFAULT_LOCALE;
};

export const I18nProvider = ({
  children,
  storageKey = 'upweo_locale',
  preferredLocale,
  allowed = LOCALE_CODES,
}: {
  children: ReactNode;
  storageKey?: string;
  /** Server-chosen locale, e.g. the storefront's negotiated language. */
  preferredLocale?: Locale;
  /** Restrict the picker — a store offering two languages must not show six. */
  allowed?: readonly Locale[];
}): React.JSX.Element => {
  const [locale, setLocaleState] = useState<Locale>(() =>
    initialLocale(storageKey, preferredLocale),
  );

  /**
   * If the stored choice is not one this store offers, fall back rather than
   * render a language the merchant has withdrawn — someone who once picked
   * French on another Upweo store should not see a French shell here.
   */
  const active = allowed.includes(locale)
    ? locale
    : (preferredLocale ?? allowed[0] ?? DEFAULT_LOCALE);

  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleState(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, next);
      }
    },
    [storageKey],
  );

  /**
   * Set `lang` and `dir` on <html>, not on a wrapper div.
   *
   * `dir` on the root is what actually flips the page for Arabic — including
   * scrollbar side, text selection and every logical CSS property. It also
   * tells screen readers which language to pronounce, and browsers which
   * hyphenation and font fallbacks to use. A wrapper div gets none of that
   * right for content rendered in portals.
   */
  const dir = isRtl(active) ? 'rtl' : 'ltr';
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = active;
      document.documentElement.dir = dir;
    }
  }, [active, dir]);

  const value = useMemo<I18nValue>(() => {
    const translator = getTranslator(active);
    return {
      locale: active,
      dir,
      t: translator.t,
      setLocale,
      formatMoney: (amount, currencyCode) => fmtMoney(amount, currencyCode, active),
      formatNumber: (n) => fmtNumber(n, active),
      formatDate: (d) => fmtDate(d, active),
      formatDateTime: (d) => fmtDateTime(d, active),
    };
  }, [active, dir, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
